/**
 * The "Sign-in" line on a VPN user's page (doc 28d §10.1): "‹provider type›
 * (customer.example)", "‹provider type› (everyone in the tenant)" or
 * "SecureLink password".
 *
 * Read from the API (`signInRoute` on GET /vpn-users/:id, VSNP-1707): the same
 * route() the access block uses after registration, with the verified domain
 * the new email-first apps find the provider by before sign-in. The browser
 * no longer works the route out itself.
 */
import { KeyRound } from 'lucide-react';
import type { VPNUserSignInRoute } from '../../services/vpnUser.service';
import { PROVIDER_TYPE_LABEL } from '../../utils/signInRouting';

const UserSignInRouteLine = ({ route }: { route?: VPNUserSignInRoute | null }) => {
  let text: string;
  let detail: string | null = null;
  let hint: string | null = null;
  if (!route) {
    text = 'unknown';
    detail = 'the API did not say (an older API, or it could not be worked out)';
  } else if (route.method === 'password') {
    text = 'SecureLink password';
  } else {
    const type = route.providerType ? PROVIDER_TYPE_LABEL[route.providerType] : 'Identity provider';
    text = `${type} (${route.domain ?? 'everyone in the tenant'})`;
    detail = route.providerName ?? route.providerAlias;
    // All mode with no verified domain of theirs: after registration they sign
    // in through the provider, but the new apps cannot find it from the email.
    if (route.reason === 'all_mode' && !route.domain) {
      hint = 'No verified domain matches this email: the new SecureLink apps ask this user for a password first.';
    }
  }

  return (
    <div data-testid="user-sign-in-route" data-method={route?.method ?? 'unknown'} data-reason={route?.reason ?? ''}>
      <p className="text-gray-900 flex items-center gap-1.5">
        <KeyRound className="w-4 h-4 text-gray-400" />
        <span>{text}</span>
        {detail && <span className="text-xs text-gray-500">— {detail}</span>}
      </p>
      {hint && <p className="mt-0.5 text-xs text-warning-700" data-testid="user-sign-in-route-hint">{hint}</p>}
    </div>
  );
};

export default UserSignInRouteLine;
